<template>
  <div id="donate-step1" class="donation-selection" v-if="product">
    <!-- Package Carousel -->
    <component :is="packageCarousel"></component>

    <!-- Donation Type -->
    <div class="tab-selector" v-if="!isAdoption && !isWildcard">
      <div
        class="single-tab"
        v-bind:class="{ selected: donationType === currentDonationType }"
        v-for="donationType in product.donationtypes"
        v-on:click="selectDonationType(donationType); logFirstTouch('donation type')"
      >
        {{ donationType == "Regular" ? "Give monthly" : "Give once" }}
      </div>
    </div>

    <!-- Donation Type For Adoption -->
    <div class="tab-selector" v-if="isAdoption">
      <div
        class="single-tab"
        v-bind:class="{ selected: !isGiftSelected }"
        v-on:click="toggleIsGift(false); logFirstTouch('gift')"
      >
        For you
      </div>
      <div
        class="single-tab"
        v-bind:class="{ selected: isGiftSelected }"
        v-on:click="toggleIsGift(true); logFirstTouch('gift')"
      >
        As a gift
      </div>
    </div>

    <!-- Dollar Handle -->
    <h2 v-if="isAdoption || isWildcard">
      {{ donation.type == "Regular" ? "Give monthly" : "Give once" }}
    </h2>
    <div class="dollar-handle-selector">
      <label
        :for="'handle-' + currentDonationType + '-' + dollarHandle.amount"
        v-for="dollarHandle in dollarHandles"
      >
        <input
          type="radio"
          v-validate="'required'"
          v-model="selectedDollarHandle"
          :name="'handle-' + currentDonationType"
          :id="'handle-' + currentDonationType + '-' + dollarHandle.amount"
          :value="dollarHandle"
          data-vv-as="amount"
          v-on:focus="logFirstTouch('dollar handle')"
        />
        <div class="amount">
          <span>{{ formatDollarHandle(dollarHandle.amount) }}</span>
        </div>
      </label>

      <!-- Custom amount -->
      <label
        :for="'handle-' + currentDonationType + '-custom'"
        v-if="proposeCustomAmount"
      >
        <input
          type="radio"
          v-validate="'required'"
          v-model="selectedDollarHandle"
          :name="'handle-' + currentDonationType"
          :id="'handle-' + currentDonationType + '-custom'"
          value="custom"
        />
        <span class="amount">
          <span>$_</span>
        </span>
      </label>
    </div>

    <!-- Custom Amount Input -->
    <div v-if="isCustomSelected">
      <div class="wwf-field icon" v-if="proposeCustomAmount">
        <div class="icon"><div>$_</div></div>
        <input
          id="customAmount"
          ref="customAmount"
          name="customAmount"
          type="number"
          v-model="donation.amount"
          v-validate="{ required: true, donationCustomAmountIsValid: true }"
          :class="{
            empty: !donation.amount,
            error:
              errors.has('customAmount') ||
              errors.has('donationCustomAmountIsValid'),
          }"
          data-vv-validate-on="change"
          data-vv-as="Custom Amount"
          v-on:focus="logFirstTouch('dollar handle')"
        />
        <label :for="'handle-' + currentDonationType + '-custom'">
          Custom amount
        </label>

        <span class="wwf-alert">
          <p
            v-if="
              errors.has('customAmount') ||
              errors.has('donationCustomAmountIsValid')
            "
          >
            {{ errors.first("customAmount") }}
          </p>
        </span>
      </div>
    </div>

    <!-- Message for max amount -->
    <div class="message-max-amount" v-show="showMaxAmountMessage">
      <p>
        For donations above $75,000 please contact our Impact Partnerships team,
        so we can be sure your gift benefits the area of conservation you are
        most passionate about.
      </p>
      <p>
        Please email
        <a href="mailto:partnerships@wwf.org.au" target="_blank"
          >partnerships@wwf.org.au</a
        >. Thank you so much for your kindness!
      </p>
    </div>

    <!-- Dollar Handle Description -->
    <div class="dollar-handle-selected" v-if="isDollarHandleSelected">
      <span>{{ selectedDollarHandle.text }}</span>
    </div>

    <span class="wwf-alert">
      <p v-if="errors.first('handle-' + currentDonationType)">
        Please choose your donation amount
      </p>
    </span>

    <!-- Tax calculator -->
    <tax-calculator
      :amount="donation.amount"
      v-if="showTaxCalculator"
    ></tax-calculator>

    <!-- Payment type -->
    <div class="payment-type-selector">
      <button
        type="button"
        class="plain"
        v-on:click="selectPaymentType('wwf_purchasetokenise')"
      >
        <span>{{ paymentTypeText }} Credit Card</span>
        <span><i class="fas fa-arrow-right"></i></span>
      </button>
      <button
        type="button"
        class="plain"
        v-on:click="selectPaymentType('wwf_paypal')"
        v-if="isPayPalAvailable"
      >
        <span>{{ paymentTypeText }} Paypal</span>
        <span><i class="fas fa-arrow-right"></i></span>
      </button>
      <button
        type="button"
        class="plain"
        v-on:click="selectPaymentType('wwf_directdebit')"
        v-if="isDirectDebitAvailable"
      >
        <span>{{ paymentTypeText }} Direct Debit</span>
        <span><i class="fas fa-arrow-right"></i></span>
      </button>
    </div>
  </div>
</template>

<script>
import { Validator } from "vee-validate";
import TaxCalculator from "../TaxCalculator.vue";
import AdoptionCarousel from "./AdoptionCarousel.vue";
import WildcardCarousel from "./WildcardCarousel.vue";
import { mapState } from "vuex";
import { getNameByDonationType } from "../../helpers";
import gtm from "../../utils/gtm";

export default {
  name: "Selection",
  components: {
    TaxCalculator,
    AdoptionCarousel,
    WildcardCarousel,
  },

  data() {
    return {
      maxAmount: 75000,
      selectedDollarHandle: null,
      formTouched: false
    };
  },
  computed: {
    packageCarousel() {
      if (this.isAdoption) {
        return "AdoptionCarousel";
      }
      if (this.isWildcard) {
        return "WildcardCarousel";
      }
      return null;
    },
    isAdoption() {
      return this.product.producttype == "adoption";
    },
    isWildcard() {
      return this.product.producttype == "wildcard";
    },
    dollarHandles() {
      return this.product.dollarhandles[this.currentDonationType];
    },
    isGiftSelected() {
      return this.donation.isGift;
    },
    isDollarHandleSelected() {
      return (
        this.selectedDollarHandle != null &&
        this.selectedDollarHandle != "custom" &&
        this.selectedDollarHandle != ""
      );
    },
    isCustomSelected() {
      return (
        this.selectedDollarHandle != null &&
        this.selectedDollarHandle === "custom"
      );
    },
    isPayPalAvailable() {
      return this.donation.type == "Single";
    },
    isDirectDebitAvailable() {
      return this.product.producttype != "wildcard";
    },
    showTaxCalculator() {
      return (
        this.donation.amount &&
        this.product.extrasetting &&
        this.product.extrasetting.displaytaxcalculator
      );
    },
    showMaxAmountMessage() {
      return (
        this.selectedDollarHandle === "custom" &&
        !!this.donation.amount &&
        !isNaN(this.donation.amount) &&
        +this.donation.amount > this.maxAmount
      );
    },
    minAmount() {
      if (
        !!this.product &&
        !!this.product.donationsettings &&
        !!this.currentDonationType &&
        !!this.product.donationsettings[this.currentDonationType]
      ) {
        return this.product.donationsettings[this.currentDonationType]
          .minimumAmount;
      } else {
        return null;
      }
    },
    proposeCustomAmount() {
      return !(
        this.product.extrasetting && this.product.extrasetting.hidecustomhandle
      );
    },
    paymentTypeText() {
      return this.isAdoption ? "Adopt with" : "Donate by";
    },
    ...mapState({
      donation: (state) => state.donation.donation,
      currentDonationType: (state) => state.donation.donation.type,
      currentDollarHandle: (state) => state.donation.donation.dollarhandle,
      currentPackage: (state) => state.donation.donation.packagecode,
      product: (state) => state.product.product,
    }),
  },
  watch: {
    selectedDollarHandle(newVal, oldVal) {
      if (this.isDollarHandleSelected) {
        this.donation.dollarhandle = this.selectedDollarHandle.amount;
        this.donation.amount = this.selectedDollarHandle.amount;
      } else {
        this.donation.dollarhandle = null;
        this.donation.amount = null;
        if (newVal === "custom") {
          // Give the focus to the input
          setTimeout(
            function () {
              this.$refs.customAmount.focus();
            }.bind(this),
            1
          );
        }
      }
    },
    currentDonationType(newVal) {
      /// Pre select the first dollar handle
      if (
        !!newVal &&
        !!this.product &&
        !!this.product.dollarhandles &&
        !!this.product.dollarhandles[newVal]
      ) {
        this.selectedDollarHandle = this.product.dollarhandles[newVal][0];
      }
    },
    currentDollarHandle(newVal) {
      // Listen for the changes from the widget
      const dh = this.dollarHandles.find((e) => e.amount === newVal);
      if (!!dh) {
        this.selectedDollarHandle = dh;
      }
    },
    currentPackage(newVal, oldVal) {
      // Listen for package change after initial value is set
      if (oldVal) {
        // Log to Amplitude when this is the first user interaction on the form
        this.logFirstTouch("choose package");
      }
    },
  },

  created() {
    // Custom validation for amount (regular/single)
    let donationCustomAmountIsValid = (amount) => {
      if (amount <= 0) {
        return {
          valid: false,
          data: { message: "The amount must be positive." },
        };
      }

      // Get the min amount from the settings of the selected type or the default one if not set
      const minimumAmount = !!this.minAmount
        ? parseFloat(this.minAmount)
        : this.type === "Regular"
        ? 15
        : 2;

      const inputAmount = parseInt(amount.replace(/ /g, ""));
      if (inputAmount < minimumAmount) {
        return {
          valid: false,
          data: { message: "The minimum amount is $" + minimumAmount },
        };
      } else if (inputAmount > this.maxAmount) {
        return {
          valid: false,
          data: { message: "The maximum amount is $75,000" },
        };
      } else {
        return {
          valid: true,
        };
      }
    };

    Validator.extend("donationCustomAmountIsValid", {
      validate: donationCustomAmountIsValid,
      getMessage: (field, params, data) => data.message,
    });
  },
  mounted() {
    /// Pre select the first dollar handle
    if (
      !!this.currentDonationType &&
      !!this.product &&
      !!this.product.dollarhandles &&
      !!this.product.dollarhandles[this.currentDonationType]
    ) {
      this.selectedDollarHandle =
        this.product.dollarhandles[this.currentDonationType][0];
    }
  },
  methods: {
    selectDonationType(type) {
      if (this.currentDonationType === type) {
        return;
      }

      this.$store.commit("updateDonation", { field: "type", value: type });

      // Reset any previous selection like if it was new
      this.$store.commit("updateDonation", {
        field: "dollarhandle",
        value: "",
      });
      this.$store.commit("updateDonation", {
        field: "amount",
        value: "",
      });

      // Update the Campaign ID and the Fund ID with the one of the selected Donation Type
      const settings = this.product.donationsettings[type];
      if (settings) {
        this.$store.commit("updateDonation", {
          field: "campaignid",
          value: settings.campaignid,
        });
        this.$store.commit("updateDonation", {
          field: "fundid",
          value: settings.fundid,
        });
      }
    },
    toggleIsGift(value) {
      this.donation.isGift = value;
    },
    formatDollarHandle(handle) {
      return `$${handle.indexOf(".00") >= 0 ? handle.slice(0, -3) : handle}`;
    },
    async selectPaymentType(paymentType) {
      try {
        console.log("Selected Payment Type", paymentType);

        // Update donation with the payment type
        this.$store.commit("updateDonation", {
          field: "payementType",
          value: paymentType,
        });

        this.logFirstTouch('payment type');

        // Validate form
        await this.validate();

        // Commit the current step (Push to database)
        await this.commit();

        // Fire GTM Event
        gtm.pushAddToCart(
          this.product.productname,
          this.product.id,
          this.donation.amount,
          this.product.producttype,
          getNameByDonationType(this.donation.type)
        );

        // Go to next step
        this.$emit("next");

        this.$store.dispatch('logFormProduct');
      } catch (err) {
        console.error("selectPaymentType - Error - ", err);
      }
    },
    validate() {
      return new Promise((resolve, reject) => {
        this.$validator
          .validateAll()
          .then((result) => {
            // Will return true if valid and false if not
            if (result) {
              resolve(result);
            } else {
              console.error("Form Selection not valid - result", result);
              reject(result);
            }
          })
          .catch((err) => {
            console.error("Step 1 Validation - Error - ", err);
            reject(err);
          });
      });
    },
    commit() {
      return new Promise((resolve, reject) => {
        // Add .00 if no . and add 0 if no 0 (eg 42.6)
        this.donation.amount =
          this.donation.amount.indexOf(".") == -1
            ? this.donation.amount.concat(".00")
            : /^\d+(\.\d{1})+$/.test(this.donation.amount)
            ? this.donation.amount.concat("0")
            : this.donation.amount;

        this.$store
          .dispatch("saveDonation")
          .then((response) => {
            resolve();
          })
          .catch((error) => {
            console.error("saveDonation1 error", error);
            reject(error);
          });
      });
    },
    logFirstTouch(fieldName) {
      if (!this.formTouched) {
        this.$store.dispatch("logFormBegin", fieldName);
        this.formTouched = true;
      }
    }
  },
};
</script>

<style scoped>
.donation-selection .dollar-handle-selected {
  margin-bottom: 2rem;
}

.donation-selection .dollar-handle-selected span {
  color: var(--wwfBlue);
}

.donation-selection .message-max-amount {
  margin-bottom: 2rem;
}

.donation-selection .message-max-amount p {
  margin-bottom: 0.5rem;
  font-size: 15px;
}

#customAmount {
  margin-bottom: 32px;
}

.donation-selection .wwf-alert {
  bottom: -11px;
}
</style>


// WEBPACK FOOTER //
// Selection.vue?aeb23cbe